import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Trophy, House } from '@phosphor-icons/react';
import ScrollFadeRow from '../components/ScrollFadeRow';
import Avatar from '../components/Avatar';
import ReactionsRow from '../components/ReactionsRow';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Compare() {
  const { workoutId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exercisesMap, setExercisesMap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [cmpRes, exRes] = await Promise.all([
          axios.get(`${API_URL}/workouts/compare/${workoutId}`, { withCredentials: true }),
          axios.get(`${API_URL}/exercises`, { withCredentials: true }),
        ]);
        setData(cmpRes.data);
        const map = {};
        exRes.data.forEach((e) => { map[e.id] = e; });
        setExercisesMap(map);
        // Defensive: de-dupe partners by profile id (server also does this)
        const uniquePartners = [];
        const seen = new Set();
        for (const p of (cmpRes.data.partners || [])) {
          if (seen.has(p.profile.id)) continue;
          seen.add(p.profile.id);
          uniquePartners.push(p);
        }
        if (uniquePartners.length > 0) {
          setSelectedPartnerId(uniquePartners[0].profile.id);
        }
        setData({ ...cmpRes.data, partners: uniquePartners });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [workoutId]);

  if (loading || !data) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-6xl font-black font-heading text-graphite">B</div>
      </div>
    );
  }

  const me = data.me;
  const partner = data.partners.find((p) => p.profile.id === selectedPartnerId);

  return (
    <div className="mobile-container min-h-screen">
      <div className="px-5 pt-10 pb-24">
        {/* Header */}
        <div className="mb-6 animate-slide-up">
          <p className="text-xs uppercase tracking-[0.24em] text-graphite/50 font-semibold mb-1">
            Session Complete
          </p>
          <h1 className="text-5xl font-black font-heading uppercase tracking-tight text-graphite leading-none">
            {data.routine_name || 'Workout'}
          </h1>
        </div>

        {/* Your solo card */}
        <div className="rounded-ios-lg p-5 mb-4 animate-slide-up shadow-ios" style={{ backgroundColor: '#C0392F', color: '#EDEAE3' }}>
          <div className="flex items-center gap-2.5 mb-1">
            <Avatar user={me.profile} size={32} tone="graphite" testId="compare-me-avatar" />
            <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-bone/70">
              You · {me.profile.display_name}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <SoloStat label="Volume" value={Math.round(me.total_volume)} unit="kg" />
            <SoloStat label="Reps" value={me.total_reps} />
            <SoloStat label="Sets" value={me.total_sets} />
          </div>
          {me.avg_rpe && (
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-bone/70 mt-3">
              Avg RPE · <span className="text-bone tabular-nums">{me.avg_rpe}</span>
            </p>
          )}
        </div>

        {/* No partners yet — only shown when this is a Squad routine and none have finished */}
        {!data.is_solo && data.partners.length === 0 && (
          <div className="ios-card p-6 mb-4 border-dashed border-2">
            <p className="text-sm text-graphite/60 text-center">
              Your training partner hasn't finished today. When they do, their stats will show up here for a head-to-head.
            </p>
          </div>
        )}

        {/* Partner picker (if multiple) */}
        {data.partners.length > 1 && (
          <ScrollFadeRow className="flex gap-2 mb-3 pb-1">
            {data.partners.map((p) => (
              <button
                key={p.profile.id}
                onClick={() => setSelectedPartnerId(p.profile.id)}
                className={`flex-shrink-0 h-10 px-4 rounded-ios-pill text-xs font-black uppercase tracking-[0.15em] ${
                  selectedPartnerId === p.profile.id
                    ? 'bg-partnerBlue text-bone'
                    : 'bg-graphite/5 text-graphite/60'
                }`}
                data-testid={`partner-tab-${p.profile.id}`}
              >
                {p.profile.display_name}
              </button>
            ))}
          </ScrollFadeRow>
        )}

        {/* Head-to-head */}
        {partner && (
          <>
            <div className="rounded-ios-lg p-5 mb-4 animate-slide-up shadow-ios" style={{ backgroundColor: '#1E5AA0', color: '#EDEAE3' }}>
              <div className="flex items-center gap-2.5 mb-1">
                <PartnerAvatarWithReactions
                  profile={partner.profile}
                  workoutId={partner.workout?.id}
                />
                <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-bone/70">
                  Partner · {partner.profile.display_name}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <SoloStat label="Volume" value={Math.round(partner.total_volume)} unit="kg" />
                <SoloStat label="Reps" value={partner.total_reps} />
                <SoloStat label="Sets" value={partner.total_sets} />
              </div>
              {partner.avg_rpe && (
                <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-bone/70 mt-3">
                  Avg RPE · <span className="text-bone tabular-nums">{partner.avg_rpe}</span>
                </p>
              )}
            </div>

            {/* Head-to-head bars */}
            <div className="ios-card p-5 mb-4">
              <div className="flex items-center gap-2 mb-4">
                <Trophy size={18} weight="fill" className="text-graphite" />
                <h2 className="text-lg font-black font-heading uppercase text-graphite">
                  Head to Head
                </h2>
              </div>
              <div className="space-y-4">
                <VersusBar
                  label="Volume"
                  meVal={me.total_volume}
                  partnerVal={partner.total_volume}
                  unit="kg"
                  meName={me.profile.display_name}
                  partnerName={partner.profile.display_name}
                />
                <VersusBar
                  label="Reps"
                  meVal={me.total_reps}
                  partnerVal={partner.total_reps}
                  meName={me.profile.display_name}
                  partnerName={partner.profile.display_name}
                />
                <VersusBar
                  label="Sets"
                  meVal={me.total_sets}
                  partnerVal={partner.total_sets}
                  meName={me.profile.display_name}
                  partnerName={partner.profile.display_name}
                />
              </div>
            </div>

            {/* Per-exercise breakdown */}
            <ExerciseBreakdown
              me={me}
              partner={partner}
              exercisesMap={exercisesMap}
            />
          </>
        )}

        <button
          onClick={() => navigate('/')}
          className="w-full h-16 bg-graphite text-bone font-black text-base uppercase tracking-[0.15em] rounded-ios-pill mt-6 flex items-center justify-center gap-2"
          data-testid="done-button"
        >
          <House size={18} weight="fill" />
          Done
        </button>
      </div>
    </div>
  );
}

function SoloStat({ label, value, unit }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-bone/60 leading-none">
        {label}
      </p>
      <p className="text-3xl font-black font-heading tabular-nums leading-tight mt-1">
        {value}
        {unit && <span className="text-sm font-normal ml-0.5">{unit}</span>}
      </p>
    </div>
  );
}

function VersusBar({ label, meVal, partnerVal, unit, meName, partnerName }) {
  const max = Math.max(meVal, partnerVal, 1);
  const mePct = (meVal / max) * 100;
  const partnerPct = (partnerVal / max) * 100;
  const diff = meVal - partnerVal;
  const winner = diff > 0 ? 'me' : diff < 0 ? 'partner' : 'tie';

  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50">
          {label}
        </p>
        {winner === 'me' && (
          <p className="text-[10px] uppercase tracking-[0.18em] font-black text-userRed">
            You + {Math.round(Math.abs(diff))}{unit}
          </p>
        )}
        {winner === 'partner' && (
          <p className="text-[10px] uppercase tracking-[0.18em] font-black text-partnerBlue">
            {partnerName} + {Math.round(Math.abs(diff))}{unit}
          </p>
        )}
        {winner === 'tie' && (
          <p className="text-[10px] uppercase tracking-[0.18em] font-black text-graphite/60">
            Tied
          </p>
        )}
      </div>
      <div className="space-y-1">
        <BarRow color="userRed" value={Math.round(meVal)} unit={unit} pct={mePct} name={meName} />
        <BarRow color="partnerBlue" value={Math.round(partnerVal)} unit={unit} pct={partnerPct} name={partnerName} />
      </div>
    </div>
  );
}

function BarRow({ color, value, unit, pct, name }) {
  const bg = color === 'userRed' ? 'bg-userRed' : 'bg-partnerBlue';
  const text = color === 'userRed' ? 'text-userRed' : 'text-partnerBlue';
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 text-[10px] uppercase tracking-[0.15em] font-bold text-graphite/60 truncate">
        {name}
      </div>
      <div className="flex-1 h-7 bg-graphite/5 rounded-ios-pill overflow-hidden">
        <div
          className={`h-full ${bg} rounded-ios-pill flex items-center justify-end px-3 transition-all duration-500`}
          style={{ width: `${Math.max(pct, 8)}%` }}
        >
          <span className="text-[11px] font-black text-bone tabular-nums">
            {value}{unit}
          </span>
        </div>
      </div>
    </div>
  );
}

function ExerciseBreakdown({ me, partner, exercisesMap }) {
  // Group sets by exercise for both people
  const groupByExercise = (sets) =>
    sets.reduce((acc, s) => {
      (acc[s.exercise_id] = acc[s.exercise_id] || []).push(s);
      return acc;
    }, {});

  const meByEx = groupByExercise(me.sets);
  const partnerByEx = groupByExercise(partner.sets);
  const allExerciseIds = Array.from(
    new Set([...Object.keys(meByEx), ...Object.keys(partnerByEx)])
  );

  if (allExerciseIds.length === 0) return null;

  return (
    <div className="ios-card p-5">
      <h2 className="text-lg font-black font-heading uppercase text-graphite mb-4">
        By Lift
      </h2>
      <div className="space-y-3">
        {allExerciseIds.map((exId) => {
          const meSets = meByEx[exId] || [];
          const partnerSets = partnerByEx[exId] || [];
          const meVol = meSets.reduce((a, s) => a + s.weight * s.reps, 0);
          const partnerVol = partnerSets.reduce((a, s) => a + s.weight * s.reps, 0);
          const exName = exercisesMap[exId]?.name || 'Exercise';
          const meTop = meSets.length ? Math.max(...meSets.map((s) => s.weight)) : 0;
          const partnerTop = partnerSets.length ? Math.max(...partnerSets.map((s) => s.weight)) : 0;

          return (
            <div key={exId} className="border-t border-hairline pt-3 first:border-t-0 first:pt-0">
              <p className="text-sm font-black uppercase tracking-tight text-graphite mb-2">
                {exName}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-userRed/70">
                    You
                  </p>
                  <p className="text-lg font-black font-heading tabular-nums text-userRed leading-tight">
                    {Math.round(meVol)}
                    <span className="text-[10px] font-normal">kg vol</span>
                  </p>
                  <p className="text-[10px] font-semibold text-graphite/50 tabular-nums">
                    Top {meTop}kg · {meSets.length} sets
                  </p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.22em] font-bold text-partnerBlue/70">
                    Partner
                  </p>
                  <p className="text-lg font-black font-heading tabular-nums text-partnerBlue leading-tight">
                    {Math.round(partnerVol)}
                    <span className="text-[10px] font-normal">kg vol</span>
                  </p>
                  <p className="text-[10px] font-semibold text-graphite/50 tabular-nums">
                    Top {partnerTop}kg · {partnerSets.length} sets
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PartnerAvatarWithReactions({ profile, workoutId }) {
  const ref = React.useRef(null);
  return (
    <>
      <span ref={ref} className="relative inline-flex">
        <Avatar user={profile} size={32} tone="graphite" testId="compare-partner-avatar" />
      </span>
      {workoutId && (
        <span className="ml-2 opacity-90">
          <ReactionsRow workoutId={workoutId} anchorRef={ref} variant="compact" testId="compare-partner-reactions" />
        </span>
      )}
    </>
  );
}
